<template>
  <div>
    <b-card title="Gerenciamento de permissões" class="mb-2">
      <p class="mb-0">
        Cadastre usuários e defina quais módulos cada um pode acessar.
      </p>
    </b-card>

    <div class="d-flex align-items-center justify-content-between">
      <b-tabs v-model="activeTab" content-class="mt-1" class="flex-grow-1">
        <b-tab title="Usuários cadastrados">
          <UserListTab
            :users="filteredUsers"
            :loading="loadingUsers"
            :fields="fields"
            :deletingUserId="deletingUserId"
            :canDeleteUser="canDeleteUser"
            :currentUserId="currentUserId"
            :permissionOptions="permissionOptions"
            @edit="goToEdit"
            @delete="deleteUser"
          />
        </b-tab>

        <b-tab :title="isEditing ? 'Editar usuário' : 'Novo usuário'">
          <UserFormTab
            :form="form"
            :isEditing="isEditing"
            :loading="loadingSave"
            :feedback="feedback"
            :allEcs="allEcs" 
            :permissionOptions="permissionOptions"
            :myPermissions="myPermissions"
            @save="saveUser"
            @cancel="resetForm"
          />
        </b-tab>

        <template #tabs-end>
          <div class="ml-auto d-flex align-items-center">
            <b-form-input
              v-if="activeTab === 0"
              v-model="searchTerm"
              placeholder="Buscar usuário..."
              size="sm"
              style="width: 250px"
            />
          </div>
        </template>
      </b-tabs>
    </div>
  </div>
</template>

<script>
import { BCard, BTabs, BTab, BFormInput } from "bootstrap-vue";
import UserListTab from "./UserListTab.vue";
import UserFormTab from "./UserFormTab.vue";
import { createUser, getUsers, updateUser, deleteUser } from '@/service/permissions/permissions'
import { getMe } from '@/service/user/user'
import { PERMISSIONS, PERMISSION_OPTIONS } from '@/enums/permissions'

export default {
  components: {
    BCard,
    BTabs,
    BTab,
    BFormInput,
    UserListTab,
    UserFormTab,
  },
  data() {
    return {
      activeTab: 0,
      searchTerm: "",
      allEcs: [],  
      users: [],
      permissions: [],
      currentUserId: null,
      myPermissions: [],
      form: {
        email: "",
        name: "",
        permissions: [],
        ecs: [],
        isAdmin: false 
      },

      isEditing: false,
      selectedUserId: null,
      deletingUserId: null,
      loadingUsers: false,
      loadingSave: false,
      feedback: {
        show: false,
        variant: "success",
        text: "",
      },

      permissionOptions: PERMISSION_OPTIONS,

      fields: [
        { key: "email", label: "E-mail", sortable: true },
        { key: "name", label: "Nome", sortable: true },
        { key: "permissions", label: "Permissões" },
        { key: "isAdmin", label: "É Admin?", class: "text-center" },
        { key: "ecs", label: "Estabelecimento" },
        { key: "actions", label: "Ações", },
      ],
    };
  },
  computed: {
    filteredUsers() {
      if (!this.searchTerm) {
        return this.users
      }

      const search = this.searchTerm.toLowerCase()

      return this.users.filter(user =>
        user.email.toLowerCase().includes(search) ||
        user.ecs.some(ec => ec.toLowerCase().includes(search))
      )
    }, 
    canDeleteUser() {
      return this.myPermissions?.includes(PERMISSIONS.MASTER)
    }
  },
  methods: {
    async loadUsers() {
    this.loadingUsers = true 

    try {
      const response = await getUsers()
      if (response?.status === 200 && Array.isArray(response.data?.data)) {
        this.users = response.data.data.map(user => ({
          id: user.id,
          name: user.name || user.email,
          email: user.email,
          permissions: Array.isArray(user.permissions) ? user.permissions.map(p => +p) : [],
          ecs: Array.isArray(user.ecs) ? user.ecs : [],
        }))
      }
    } finally {
      this.loadingUsers = false 
      }
    },

    async loadMe() {
      const response = await getMe()

      if (response?.data) {
        this.currentUserId = response.data.id
        this.allEcs = response.data.merchants.map(m => ({
          value: m.veripag_id, 
          name: `${m.name} (${m.veripag_id})`,
        }))
        this.myPermissions = Array.isArray(response.data.permissions)
      ? response.data.permissions.map(p => +p)
      : []
      }
    },

    goToEdit(user) {
      this.isEditing = true;
      this.selectedUserId = user.id;

      this.form = {
        name: user.name || user.email,
        email: user.email,
        permissions: Array.isArray(user.permissions) ? user.permissions.map(p => +p) : [],
        ecs: Array.isArray(user.ecs) ? [...user.ecs] : [],
        isAdmin: user.permissions.includes(PERMISSIONS.ADMIN),
      };

      this.activeTab = 1;
    },

  async saveUser() {
    this.loadingSave = true 

    try {
      let permissions = [...this.form.permissions]

      if (this.form.isAdmin) {
        if (!permissions.includes(PERMISSIONS.ADMIN)) {
          permissions.push(PERMISSIONS.ADMIN)
        }
      } else {
        permissions = permissions.filter(p => p !== PERMISSIONS.ADMIN)
      }

      const payload = {
        email: this.form.email,
        name: this.form.name,
        permissions: permissions.map(p => +p),
        ecs: this.form.ecs.map(ec => String(ec))
      }

      let response

      if (this.isEditing) {
        response = await updateUser(this.selectedUserId, payload)
      } else {
        response = await createUser(payload)
      }

      if (response?.status === 200 || response?.status === 201) {
        this.$bvToast.toast('Usuário salvo com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })

        await this.loadUsers()
        this.resetForm()
        this.activeTab = 0
        return
      }

      if (response?.status === 422) {
        const hasEmailUniqueError = response?.data?.errors?.some(
          err => err.field === 'email' && err.rule === 'unique'
        )

        if (hasEmailUniqueError) {
          this.$bvToast.toast(
            'Esse e-mail já foi cadastrado. Caso queira ativá-lo, entre em contato com o suporte.',
            {
              title: 'E-mail duplicado',
              variant: 'danger',
              solid: true
            }
          )
          return
        }
      }

      this.$bvToast.toast('Erro ao salvar usuário.', {
        title: 'Erro',
        variant: 'danger',
        solid: true
      })

    } finally {
      this.loadingSave = false 
    }
  },

    resetForm() {
      this.form = {
        name: "",
        email: "",
        permissions: [],
        ecs: [],
      };

      this.isEditing = false;
      this.selectedUserId = null;
    },

    async deleteUser(id) {
    try {
      this.deletingUserId = id

      const response = await deleteUser(id)

      if (response?.status === 200 || response?.status === 204) {

        this.$bvToast.toast('Usuário excluído com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })

        await this.loadUsers()
      }                   

    } catch (error) {
      this.$bvToast.toast('Erro ao excluir usuário', {
        title: 'Erro',
        variant: 'danger',
        solid: true
      })
    } finally {
      this.deletingUserId = null
    }
  }
  },
    mounted() {
      this.loadMe()
      this.loadUsers()
    },
};
</script>